import React from 'react';
import {
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography
} from '@mui/material';
import {
  Person as PersonIcon,
  Cake as CakeIcon,
  Tag as TagIcon
} from '@mui/icons-material';
import type { Person } from '../../../../types';

interface BasicInfoPanelProps {
  person: Person;
  showMetadata?: boolean;
  metadataFormat?: 'short' | 'long';
}

/**
 * BasicInfoPanel - Displays basic identity information
 *
 * Shows:
 * - Full name (all variations)
 * - Nicknames
 * - Gender
 * - Generation, Family Line
 * - Life span
 */
export const BasicInfoPanel: React.FC<BasicInfoPanelProps> = ({
  person,
  showMetadata = false,
  metadataFormat = 'short'
}) => {
  const getLifeSpan = (): string => {
    const birthYear = person.birthDate ? new Date(person.birthDate).getFullYear() : null;
    const deathYear = person.deathDate ? new Date(person.deathDate).getFullYear() : null;

    if (birthYear && deathYear) {
      return `${birthYear} - ${deathYear}`;
    } else if (birthYear) {
      return `b. ${birthYear}`;
    } else if (deathYear) {
      return `d. ${deathYear}`;
    }
    return '';
  };

  const getFullName = (): string => {
    return `${person.firstName} ${person.middleName || ''} ${person.lastName} ${person.suffix || ''}`.trim();
  };

  const lifeSpan = getLifeSpan();

  return (
    <Box>
      <List dense>
        {/* Full Name */}
        <ListItem>
          <ListItemIcon>
            <PersonIcon />
          </ListItemIcon>
          <ListItemText
            primary="Full Name"
            secondary={getFullName()}
          />
        </ListItem>

        {/* Maiden Name */}
        {person.maidenName && (
          <ListItem>
            <ListItemIcon>
              <PersonIcon />
            </ListItemIcon>
            <ListItemText
              primary="Maiden Name"
              secondary={person.maidenName}
            />
          </ListItem>
        )}

        {/* Nicknames */}
        {person.nicknames && person.nicknames.length > 0 && (
          <ListItem>
            <ListItemIcon>
              <TagIcon />
            </ListItemIcon>
            <ListItemText
              primary="Nicknames"
              secondary={person.nicknames.join(', ')}
            />
          </ListItem>
        )}

        {/* Gender */}
        <ListItem>
          <ListItemIcon>
            <PersonIcon />
          </ListItemIcon>
          <ListItemText
            primary="Gender"
            secondary={person.gender}
          />
        </ListItem>

        {/* Life Span */}
        {lifeSpan && (
          <ListItem>
            <ListItemIcon>
              <CakeIcon />
            </ListItemIcon>
            <ListItemText
              primary="Life Span"
              secondary={lifeSpan}
            />
          </ListItem>
        )}

        {/* Generation */}
        {person.generation !== undefined && (
          <ListItem>
            <ListItemIcon>
              <PersonIcon />
            </ListItemIcon>
            <ListItemText
              primary="Generation"
              secondary={`Generation ${person.generation}`}
            />
          </ListItem>
        )}

        {/* Family Line */}
        {person.familyLine && (
          <ListItem>
            <ListItemIcon>
              <PersonIcon />
            </ListItemIcon>
            <ListItemText
              primary="Family Line"
              secondary={person.familyLine}
            />
          </ListItem>
        )}
      </List>

      {/* Empty state */}
      {!getFullName() && !person.gender && !lifeSpan && (
        <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
          No basic information available
        </Typography>
      )}
    </Box>
  );
};
